<template>
  <v-row>
    <v-row>
      <v-col class="align-left">
        <ExpandCollapseToggle
          id="jointsPanelToggle"
          @toggleAllPanels="toggleAllPanels"
          styleClass="mt-3"
          :panel="panel"
        />
      </v-col>
      <v-col class="align-right mt-2">
        <ValidationCountsHeader v-if="moduleType == MODULE_TYPES.MANAGEMENT" />
      </v-col>
    </v-row>
    <v-expansion-panels v-model="panel" multiple color="#fff">
      <v-expansion-panel class="mt-5">
        <v-expansion-panel-title
          class="d-flex justify-start"
          hide-actions
          expand-icon="fa-solid fa-plus"
          collapse-icon="fa-solid fa-minus"
        >
          <v-icon v-if="!panel.includes(0)" icon="fa-solid fa-plus" />
          <v-icon v-if="panel.includes(0)" icon="fa-solid fa-minus" />
          <h3>Joint Conditions</h3>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>
          <v-form ref="jointConditions" v-model="valid">
            <LabelSelect
              :labelXl="3"
              :labelLg="4"
              :labelmd="4"
              :labelsm="5"
              :selectXl="3"
              :selectLg="4"
              :selectMd="5"
              :selectSm="6"
              id="jointRating"
              label="B.C.08 Overall Joint Condition Rating"
              :refTable="REFERENCE_TABLE.JOINT_CONDITION_RATING"
              :isEditable="isEditable"
              :isRequired="isEditable"
              v-model="jointsValue.overallCondRating"
            ></LabelSelect>
            <v-row>
              <v-col sm="12">
                <CommentTypeTextField
                  v-if="moduleType == MODULE_TYPES.INSPECTION"
                  label="Overall Joint Notes"
                  id="field_Overall_Joint_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.JOINTS_TINSP_COMMENTS"
                  :readonly="!isEditable"
                  :counter="inspCommentMaxLength"
                  :maxlength="inspCommentMaxLength"
                  @changesDetected="inspectionStore.setDirtyFlag(true)"
                />
                <ManagementCommentTypeTextField
                  v-else
                  label="Overall Joint Notes"
                  id="field_Overall_Joint_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.JOINTS_TINSP_COMMENTS"
                  :readonly="!isEditable"
                  :counter="inspCommentMaxLength"
                  :maxlength="inspCommentMaxLength"
                  :comments="jointsValue.inspectionComments"
                  :brkey="jointsValue.brkey"
                  :inspectionId="jointsValue.inspectionId"
                  @updateComment="(comment) => setUpdatedComment(comment, INSPECTION_COMMENTS.JOINTS_TINSP_COMMENTS)"
                />
              </v-col>
            </v-row>
            <div style="display: flex">
              <v-btn
                id="btn_addNewJoint"
                variant="outlined"
                class="my-2 d-inline"
                @click="addNewJoint"
                :disabled="!isEditable || anyJointDetailEditable"
                >Add New Joint</v-btn
              >
              <div style="display: flex">
                <v-checkbox
                  class="pl-6 pt-2 d-inline showAllCheckbox"
                  id="checkbox_SHOW_REPLACEMENT_JOINTS"
                  hide-details
                  :modelValue="showAllJoints"
                  :disabled="anyJointDetailEditable || !hasReplacedJointDetails()"
                  @update:modelValue="
                    ($event) => {
                      showAllJoints = $event;
                      if (showAllJoints) {
                        getAllJointDetails();
                      } else {
                        getActiveJointDetails();
                      }
                    }
                  "
                >
                </v-checkbox>
                <span class="pl-2 d-inline font-weight-bold" style="padding-top: 16px">Show Replaced Joints</span>
              </div>
            </div>

            <v-row class="mb-8">
              <v-col sm="12">
                <v-data-table
                  id="joints-data-table"
                  v-model:page="pageInspection.page"
                  :items-per-page="pageInspection.itemsPerPage"
                  v-model:expanded="expandedRows"
                  :headers="headers"
                  :items="joints"
                  class="compactTable lessPaddingTableColumns"
                  v-model:sort-by="sortBy"
                  hide-default-footer
                  must-sort
                  show-expand
                  return-object
                  @update:sort-by="jointsFooter.setPageValue(1)"
                >
                  <template v-slot:[`header.data-table-expand`]="{ column }">
                    {{ column.title }}
                  </template>
                  <template v-slot:[`header.jointId`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.recordKey`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointType`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.bearingType`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointLocation`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointMovement`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointDebrisImpaction`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointLeaking`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointDamage`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.jointCovered`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.conditionRating`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>

                  <template v-slot:[`header.action`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>

                  <template v-slot:item="{ item }">
                    <tr>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <template v-if="!anyJointDetailEditable">
                          <button
                            v-if="!expandedRows.includes(item)"
                            @click="toggleRow(item)"
                            :id="`button_Chevron_Right_${item.uniqueKey}`"
                            :aria-describedby="`button_Chevron_Right_${item.uniqueKey}`"
                            :aria-label="`button_Chevron_Right_${item.uniqueKey}`"
                          >
                            <v-icon
                              :id="`icon_Chevron_Right_${item.uniqueKey}`"
                              icon="fa-solid fa-chevron-right"
                            ></v-icon>
                          </button>

                          <button
                            v-if="expandedRows.includes(item)"
                            @click="toggleRow(item)"
                            :id="`button_Chevron_Down_${item.uniqueKey}`"
                            :aria-describedby="`button_Chevron_Down_${item.uniqueKey}`"
                            :aria-label="`button_Chevron_Down_${item.uniqueKey}`"
                          >
                            <v-icon
                              :id="`icon_Chevron_Down_${item.uniqueKey}`"
                              icon="fa-solid fa-chevron-down"
                            ></v-icon>
                          </button>
                        </template>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        {{ item.jointId }}
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        {{ item.recordKey }}
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>{{
                            item.jointType
                              ? configStore.getReferenceValue(REFERENCE_TABLE.JOINT_TYPE, item.jointType)
                              : ""
                          }}</span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>{{
                            jointsValue.jointLocations?.find((a) => a.value === item.jointLocation)?.title
                          }}</span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>{{
                            item.jointMovement
                              ? configStore.getReferenceValue(REFERENCE_TABLE.JOINT_MOVEMENT, item.jointMovement)
                              : ""
                          }}</span>
                        </div>
                      </td>

                      <td :class="rowClass(item)">
                        <v-checkbox
                          hide-details
                          :aria-label="`checkbox_${item.uniqueKey}_DebrisImpact`"
                          :true-value="'1'"
                          disabled
                          v-model="item.debrisImpact"
                          class="justify-center"
                        ></v-checkbox>
                      </td>
                      <td :class="rowClass(item)">
                        <v-checkbox
                          :aria-label="`checkbox_${item.uniqueKey}_Leaking`"
                          hide-details
                          :true-value="'1'"
                          disabled
                          v-model="item.leaking"
                          class="justify-center"
                        ></v-checkbox>
                      </td>
                      <td :class="rowClass(item)">
                        <v-checkbox
                          :aria-label="`checkbox_${item.uniqueKey}_Damage`"
                          hide-details
                          :true-value="'1'"
                          disabled
                          v-model="item.damage"
                          class="justify-center"
                        ></v-checkbox>
                      </td>
                      <td :class="rowClass(item)">
                        <v-checkbox
                          :aria-label="`checkbox_${item.uniqueKey}_Covered`"
                          hide-details
                          :true-value="'1'"
                          disabled
                          v-model="item.covered"
                          class="justify-center"
                        ></v-checkbox>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>{{
                            item.conditionRating
                              ? configStore.getReferenceValue(
                                  REFERENCE_TABLE.JOINT_CONDITION_RATING,
                                  item.conditionRating
                                )
                              : ""
                          }}</span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'" v-if="isEditable">
                        <div style="white-space: nowrap" v-if="!item.readOnly && anyJointDetailEditable && isEditable">
                          <IconTooltip
                            id="icon_add"
                            icon="fa-solid fa-check"
                            :tooltip="item.isAdd ? 'Add' : 'Update'"
                            @click="saveDetail(item)"
                          />
                          <IconTooltip id="icon_close" icon="fa-solid fa-xmark" tooltip="Close" @click="closeDetail" />
                        </div>
                        <div style="white-space: nowrap" v-else-if="isEditable && !anyJointDetailEditable">
                          <IconTooltip
                            id="icon_edit"
                            icon="fa-solid fa-pencil"
                            tooltip="Edit"
                            :disabled="isNotCurrent(item)"
                            @click="editDetail(item)"
                          />
                          <IconTooltip
                            id="icon_replace"
                            icon="fa-solid fa-arrows-rotate"
                            tooltip="Replace"
                            :disabled="isNotCurrent(item)"
                            @click="replaceDetail(item)"
                          />
                          <IconTooltip
                            id="icon_delete"
                            icon="fa-solid fa-trash"
                            tooltip="Delete"
                            @click="deleteDetail(item)"
                            :disabled="isNotCurrent(item)"
                          />
                        </div>
                      </td>
                    </tr>
                  </template>

                  <template v-slot:expanded-row="{ item }">
                    <td :colspan="headers.length">
                      <v-form ref="detailForm" v-model="validDetails" class="mt-2" v-if="$vuetify.display.lgAndUp">
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelText
                              :labelXl="5"
                              :labelLg="6"
                              :textXl="6"
                              :textLg="5"
                              id="jointId"
                              label="JT.01 Joint Key"
                              v-model="item.jointId"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelText
                              :labelXl="5"
                              :labelLg="6"
                              :textXl="6"
                              :textLg="5"
                              id="jointLength"
                              label="JT.07 Joint Length"
                              :allowCommas="false"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointLength"
                              @update:modelValue="changesDetected = true"
                              type="number"
                              :maxlength="10"
                              appendWith=" ft"
                            ></LabelText>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelText
                              :labelXl="5"
                              :labelLg="6"
                              :textXl="6"
                              :textLg="5"
                              id="recordKey"
                              label="JT.02 Record Key"
                              v-model="item.recordKey"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelCheckbox
                              :labelXl="5"
                              :labelLg="6"
                              id="impaction"
                              label="JT.08 Debris Impaction?"
                              :showColon="false"
                              editIndent
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.debrisImpact"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelSelect
                              :labelXl="5"
                              :labelLg="6"
                              :selectXl="6"
                              :selectLg="5"
                              id="jointType"
                              label="JT.03 Joint Type"
                              :refTable="REFERENCE_TABLE.JOINT_TYPE"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointType"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelCheckbox
                              :labelXl="5"
                              :labelLg="6"
                              :showColon="false"
                              editIndent
                              id="leaking"
                              label="JT.09 Leaking?"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.leaking"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelSelect
                              :labelXl="5"
                              :labelLg="6"
                              :selectXl="6"
                              :selectLg="5"
                              id="jointLocation"
                              label="JT.04 Joint Location"
                              :options="jointsValue.jointLocations"
                              :isEditable="
                                (isEditable && !item.readOnly && !item.isAdd && !isReplacement(item)) ||
                                (isEditable && item.isAdd && !item.isReplacing)
                              "
                              v-model="item.jointLocation"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelCheckbox
                              :labelXl="5"
                              :labelLg="6"
                              id="damage"
                              label="JT.10 Damage?"
                              :showColon="false"
                              editIndent
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.damage"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelSelect
                              :labelXl="5"
                              :labelLg="6"
                              :selectXl="6"
                              :selectLg="5"
                              id="jointMovement"
                              label="JT.05 Joint Movement"
                              :refTable="REFERENCE_TABLE.JOINT_MOVEMENT"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointMovement"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelCheckbox
                              class="py-0"
                              :labelXl="5"
                              :labelLg="6"
                              id="covered"
                              label="JT.11 Covered?"
                              :showColon="false"
                              editIndent
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.covered"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0 pb-3">
                            <LabelSelect
                              :labelXl="5"
                              :labelLg="6"
                              :selectXl="6"
                              :selectLg="5"
                              id="jointManufacturer"
                              label="JT.06 Joint Manufacturer"
                              :refTable="REFERENCE_TABLE.JOINT_MANUFACTURER"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointManufacturer"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="pt-1 pb-3 px-0">
                            <LabelSelect
                              :labelXl="5"
                              :labelLg="6"
                              :selectXl="6"
                              :selectLg="5"
                              id="jointCondRating"
                              label="JT.12 Condition Rating"
                              :refTable="REFERENCE_TABLE.JOINT_CONDITION_RATING"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.conditionRating"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" class="pl-0 pb-0">
                            <LabelText
                              labelXl="11"
                              labelLg="11"
                              textXl="12"
                              textLg="12"
                              type="textarea"
                              label="JT.13 Joint Notes"
                              id="jointNotes"
                              v-model="item.jointComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="jointCommentMaxLength"
                              :maxlength="jointCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelText
                              :labelXl="5"
                              :labelLg="6"
                              :textXl="6"
                              :textLg="5"
                              id="installYear"
                              label="JT.14 Extrusion Install Year"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.extrusionInstallDate"
                              type="number"
                              :maxlength="4"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1901, 2999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0">
                            <LabelText
                              :labelXl="5"
                              :labelLg="6"
                              :textXl="6"
                              :textLg="5"
                              id="ecmsNo"
                              label="JT.16 ECMS No"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.ecmsId"
                              type="number"
                              :maxlength="6"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                            ></LabelText>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="6" lg="6" class="px-0 pb-2">
                            <LabelText
                              :labelXl="5"
                              :labelLg="6"
                              :textXl="6"
                              :textLg="5"
                              id="sealInstallYear"
                              label="JT.15 Seal Install Year"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.sealInstallDate"
                              type="number"
                              :maxlength="4"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1901, 2999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" xl="6" lg="6" class="px-0 pb-2">
                            <LabelSelect
                              :labelXl="5"
                              :labelLg="6"
                              :selectXl="6"
                              :selectLg="5"
                              :isRequired="item.isReplacing"
                              id="replacementReason"
                              label="JT.17 Replacement Reason"
                              :refTable="REFERENCE_TABLE.JOINT_REPLACEMENT_REASON"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.replacementReason"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12" xl="12" lg="12">
                            <LabelText
                              labelXl="11"
                              labelLg="11"
                              textXl="12"
                              textLg="12"
                              type="textarea"
                              label="JT.18 Replacement Notes"
                              id="replacementNotes"
                              v-model="item.replacementComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="jointCommentMaxLength"
                              :maxlength="jointCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                        </v-row>
                      </v-form>
                      <v-form ref="detailForm" v-model="validDetails" class="mt-2" v-else>
                        <v-row>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="5"
                              :textMd="8"
                              :textSm="7"
                              id="jointId"
                              label="JT.01 Joint Key"
                              v-model="item.jointId"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="5"
                              :textMd="8"
                              :textSm="7"
                              id="recordKey"
                              label="JT.02 Record Key"
                              v-model="item.recordKey"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="5"
                              :selectMd="8"
                              :selectSm="7"
                              id="jointType"
                              label="JT.03 Joint Type"
                              :refTable="REFERENCE_TABLE.JOINT_TYPE"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointType"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="5"
                              :selectMd="8"
                              :selectSm="7"
                              id="jointLocation"
                              label="JT.04 Joint Location"
                              :options="jointsValue.jointLocations"
                              :isEditable="
                                (isEditable && !item.readOnly && !item.isAdd && !isReplacement(item)) ||
                                (isEditable && item.isAdd && !item.isReplacing)
                              "
                              v-model="item.jointLocation"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="5"
                              :selectMd="8"
                              :selectSm="7"
                              id="jointMovement"
                              label="JT.05 Joint Movement"
                              :refTable="REFERENCE_TABLE.JOINT_MOVEMENT"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointMovement"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="5"
                              :selectMd="8"
                              :selectSm="7"
                              id="jointManufacturer"
                              label="JT.06 Joint Manufacturer"
                              :refTable="REFERENCE_TABLE.JOINT_MANUFACTURER"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointManufacturer"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="5"
                              :textMd="8"
                              :textSm="7"
                              id="jointLength"
                              label="JT.07 Joint Length"
                              :allowCommas="false"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.jointLength"
                              @update:modelValue="changesDetected = true"
                              type="number"
                              :maxlength="10"
                              appendWith=" ft"
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelCheckbox
                              :labelMd="4"
                              :labelSm="5"
                              id="impaction"
                              label="JT.08 Debris Impaction?"
                              :showColon="false"
                              editIndent
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.debrisImpact"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelCheckbox
                              :labelMd="4"
                              :labelSm="5"
                              :showColon="false"
                              editIndent
                              id="leaking"
                              label="JT.09 Leaking?"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.leaking"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelCheckbox
                              :labelMd="4"
                              :labelSm="5"
                              id="damage"
                              label="JT.10 Damage?"
                              :showColon="false"
                              editIndent
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.damage"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelCheckbox
                              class="py-0"
                              :labelMd="4"
                              :labelSm="5"
                              id="covered"
                              label="JT.11 Covered?"
                              :showColon="false"
                              editIndent
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.covered"
                              @update:modelValue="changesDetected = true"
                            ></LabelCheckbox>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="5"
                              :selectMd="8"
                              :selectSm="7"
                              id="jointCondRating"
                              label="JT.12 Condition Rating"
                              :refTable="REFERENCE_TABLE.JOINT_CONDITION_RATING"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.conditionRating"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              labelMd="11"
                              labelSm="11"
                              textMd="12"
                              textSm="12"
                              type="textarea"
                              label="JT.13 Joint Notes"
                              id="jointNotes"
                              v-model="item.jointComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="jointCommentMaxLength"
                              :maxlength="jointCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="5"
                              :textMd="8"
                              :textSm="7"
                              id="installYear"
                              label="JT.14 Extrusion Install Year"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.extrusionInstallDate"
                              type="number"
                              :maxlength="4"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1901, 2999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="5"
                              :textMd="8"
                              :textSm="7"
                              id="sealInstallYear"
                              label="JT.15 Seal Install Year"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.sealInstallDate"
                              type="number"
                              :maxlength="4"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1901, 2999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="5"
                              :textMd="8"
                              :textSm="7"
                              id="ecmsNo"
                              label="JT.16 ECMS No"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.ecmsId"
                              type="number"
                              :maxlength="6"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="5"
                              :selectMd="8"
                              :selectSm="7"
                              :isRequired="item.isReplacing"
                              id="replacementReason"
                              label="JT.17 Replacement Reason"
                              :refTable="REFERENCE_TABLE.JOINT_REPLACEMENT_REASON"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.replacementReason"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              labelMd="12"
                              labelSm="12"
                              textMd="12"
                              textSm="12"
                              type="textarea"
                              label="JT.18 Replacement Notes"
                              id="replacementNotes"
                              v-model="item.replacementComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="jointCommentMaxLength"
                              :maxlength="jointCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                        </v-row>
                      </v-form>
                    </td>
                  </template>
                  <template v-slot:bottom>
                    <DataTableFooter
                      ref="jointsFooter"
                      id="inspection_joints_footer"
                      :items="joints"
                      v-model="pageInspection"
                    ></DataTableFooter>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-form>
        </v-expansion-panel-text>
      </v-expansion-panel> </v-expansion-panels
  ></v-row>
  <ConfirmDialog ref="closeConfirm" :cancelButtonText="BUTTONS.NO" />
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useInspectionStore } from "@/stores/inspection";
import LabelSelect from "@/components/common/LabelSelect.vue";
import LabelText from "@/components/common/LabelText.vue";
import LabelCheckbox from "@/components/common/LabelCheckbox.vue";

import CommentTypeTextField from "@/components/common/CommentTypeTextField.vue";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { clone } from "@/util/clone";
import IconTooltip from "@/components/common/IconTooltip.vue";
import { useConfigStore } from "@/stores/config";
import { convertToInt } from "@/composables/util";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import { LOGGER } from "@/util/logger";
import ConfirmDialog from "@/components/common/ConfirmDialog.vue";
import { INSPECTION_MESSAGES, MODULE_TYPES } from "@/constants/InspectionConstants";
import { BUTTONS } from "@/constants/CommonWebConstants";
import joint from "@/data/joint";
import DataTableHeader from "@/components/common/DataTableHeader.vue";
import { valueInRangeRule } from "@/composables/validationRules";
import DataTableFooter from "@/components/common/DataTableFooter.vue";
import { scrollToDataTable } from "@/util/scrollToElement";
import ExpandCollapseToggle from "@/components/shared/ExpandCollapseToggle.vue";
import { useSnackbarStore } from "@/stores/snackbar";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { storeToRefs } from "pinia";
import { useJointsStore } from "@/components/inspection/store/joints";
import { useInspectionLinksStore } from "@/stores/inspectionLinks";
import { INSPECTION_LINKS_JOINTS_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { useUserPermissionStore } from "@/stores/userPermission";
import ManagementCommentTypeTextField from "@/components/common/ManagementCommentTypeTextField.vue";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { SNACKBAR_MARKER } from "@/constants/GlobalSnackbar";
import ValidationCountsHeader from "@/components/shared/ValidationCountsHeader.vue";

export default {
  name: "InspectionJoints",
  props: {
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  emits: ["showMessage"],

  data() {
    return {
      headers: [
        {
          key: "data-table-expand",
          width: "1%",
        },
        {
          line1: "JT.01",
          line2: "Joint",
          line3: "Key",
          key: "jointId",
          sortable: true,
          width: "12%",
        },
        {
          line1: "JT.02",
          line2: "Record",
          line3: "Key",
          key: "recordKey",
          sortable: false,
        },
        {
          line1: "JT.03",
          line2: "Joint",
          line3: "Type",
          key: "jointType",
          sortable: false,
          width: "10%",
        },
        {
          line1: "JT.04",
          line2: "Joint",
          line3: "Location",
          key: "jointLocation",
          sortable: false,
          width: "10%",
        },
        {
          line1: "JT.05",
          line2: "Joint",
          line3: "Movement",
          key: "jointMovement",
          sortable: false,
          width: "10%",
        },
        {
          line1: "JT.08",
          line2: "Debris",
          line3: "Impaction?",
          key: "jointDebrisImpaction",
          sortable: false,
          width: "8%",
        },
        {
          line1: "JT.09",
          line2: "Leaking?",
          line3: null,
          key: "jointLeaking",
          sortable: false,
          width: "8%",
        },
        {
          line1: "JT.10",
          line2: "Damage?",
          line3: null,
          key: "jointDamage",
          sortable: false,
          width: "8%",
        },
        {
          line1: "JT.11",
          line2: "Covered?",
          line3: null,
          key: "jointCovered",
          sortable: false,
          width: "8%",
        },
        {
          line1: "JT.12",
          line2: "Condition",
          line3: "Rating",
          key: "conditionRating",
          sortable: true,
          width: "15%",
        },
        {
          line1: null,
          line2: "Action",
          line3: null,
          key: "action",
          sortable: false,
        },
      ],
    };
  },
  setup(props) {
    const allPanels = [0];
    let panel = ref(allPanels);
    const jointsStore = useJointsStore();
    const inspectionLinksStore = useInspectionLinksStore();
    const inspectionStore = useInspectionStore();
    const snackbarStore = useSnackbarStore();
    const userPermissionStore = useUserPermissionStore();
    const quickSearchStore = useQuickSearchStore();
    const { hasQuickSearchSucceeded } = storeToRefs(quickSearchStore);

    let structure = computed(() => inspectionStore.selectedInspection);
    const isEditable = computed(() => {
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        return hasQuickSearchSucceeded.value && jointsValue?.value?.brkey && hasAccessToEdit.value;
      } else {
        return inspectionStore.isEditable;
      }
    });

    const hasAccessToEdit = computed({
      get() {
        if (structureHeader.value?.INSPECTIONSTATUS === INSPECTION_STATUSES.ACCEPTED) {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_JOINTS_PERMISSIONS[ACCESS_LEVELS.CUSTOM.EDIT_APPROVED_INSPECTION],
            userPermissionStore.getUserPermissionByBrkeyFromCache(jointsValue.value.brkey)
          );
        } else {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_JOINTS_PERMISSIONS[ACCESS_LEVELS.EDIT],
            userPermissionStore.getUserPermissionByBrkeyFromCache(jointsValue.value.brkey)
          );
        }
      },
    });

    const jointCommentMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.JOINT_COMMENT_MAX_LENGTH);
    });
    const inspCommentMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.INSPECTION_COMMENT_MAX_LENGTH);
    });

    let joints = ref([]);
    let expandedRows = ref([]);
    const configStore = useConfigStore();
    let snackBarId = ref("");
    let jointsValue = ref({});
    const valid = ref(true);
    const jointConditions = ref(null);
    const getNewJoint = () => {
      return clone(joint);
    };
    const structureHeader = computed(() => quickSearchStore.getStructure());
    let detailForm = ref();
    let validDetails = ref(true);
    let changesDetected = ref(false);
    let showAllJoints = ref(false);
    const closeConfirm = ref(null);
    let brkey = ref("");
    let inspectionId = ref("");
    let resetDirty = ref(false);
    const pageInspection = ref({
      itemsPerPage: 30,
      page: 1,
    });

    let jointsFooter = ref(null);
    let previousSort = ref([{ key: "jointId", order: "asc" }]);
    let sortBy = ref([{ key: "jointId", order: "asc" }]);
    async function validate() {
      jointConditions.value.validate();
      detailForm.value?.validate();
    }
    onMounted(async () => {
      await readData();
    });
    const onRowExpand = (event) => {
      //on clicking on row expansion icon - set the data to readonly.
      event.data.readOnly = true;
    };

    const sort = (arr) => {
      arr.value = arr.value?.sort(function (a, b) {
        if (a.jointId == b.jointId) {
          return convertToInt(b.recordKey) - convertToInt(a.recordKey);
        } else {
          return a.jointId - b.jointId;
        }
      });
    };

    let validationSnackbar = computed({
      get() {
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          return inspectionLinksStore.showValidationSnackbar;
        } else {
          return inspectionStore.getShowValidationSnackbar;
        }
      },
      set(value) {
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          inspectionLinksStore.showValidationSnackbar = value;
        } else {
          inspectionStore.setShowValidationSnackbar(value);
        }
      },
    });

    const readData = async () => {
      jointsValue.value = {};
      resetDirty.value = true;
      brkey.value = "";
      inspectionId.value = "";
      if (props.moduleType == MODULE_TYPES.MANAGEMENT && structureHeader.value?.BRKEY) {
        if (!hasQuickSearchSucceeded.value) {
          resetDirty.value = true;
          return;
        }
        await jointsStore.loadDataObject(
          props.moduleType,
          structureHeader.value.BRKEY,
          structureHeader.value.INSPECTIONID
        );
        jointsValue.value = jointsStore.getJoints();
      } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
        brkey.value = structure.value.structureBridge.brkey;
        inspectionId.value = structure.value.inspection.inspectionId;
        await jointsStore.loadDataObject(props.moduleType, null, null, structure.value);
        jointsValue.value = jointsStore.getJoints();
      }
      getJointDetails();
      resetDirty.value = true;
      await validate();
    };

    const saveChanges = async () => {
      const isSuccess = await jointsStore.saveData(props.moduleType, jointsValue.value, structure.value);
      if (isSuccess) {
        inspectionLinksStore.isDirty = false;
        if (props.moduleType == MODULE_TYPES.INSPECTION) {
          await jointsStore.loadDataObject(props.moduleType, null, null, structure.value);
        }
        jointsValue.value = jointsStore.getJoints();
        getJointDetails();
        // Using this flag to not set the dirty flag in watch
        resetDirty.value = true;
      }
      return isSuccess;
    };

    const cancelChanges = async () => {
      //only called from Inspection Links
      jointsValue.value = jointsStore.getJoints();
      getJointDetails();
      // Using this flag to not set the dirty flag in watch
      resetDirty.value = true;
      inspectionLinksStore.isDirty = false;
    };

    const setUpdatedComment = (comment, commentType) => {
      const foundComment = jointsValue.value.inspectionComments.find((c) => {
        return c.commentType === commentType;
      });
      if (foundComment) {
        foundComment.notes = comment.notes;
      } else {
        jointsValue.value.inspectionComments.push(comment);
      }
    };

    const isNotCurrent = (details) => {
      //check for replacements
      const maxRecordkey = Math.max(
        ...joints.value.filter((a) => a.jointId == details.jointId).map((a) => a.recordKey)
      );
      return details.recordKey != maxRecordkey;
    };

    const isReplacement = (details) => {
      //check if if replacement
      const minRecordkey = Math.min(
        ...joints.value.filter((a) => a.jointId == details.jointId).map((a) => a.recordKey)
      );
      return details.recordKey != minRecordkey;
    };

    const cloneCurrentInspectionJoints = () => {
      let jointsClone = clone(jointsValue.value?.structureJoints);

      if (jointsClone) {
        //remove null values from array(if any)
        jointsClone = jointsClone?.filter((a) => !!a);
      }

      return jointsClone;
    };

    const getAllJointDetails = () => {
      expandedRows.value = [];
      joints.value = cloneCurrentInspectionJoints();
      //set unique key for toggle expansion
      for (const element of joints.value ?? []) {
        element.uniqueKey = element.jointId + "-" + element.recordKey;
        element.readOnly = true;
      }
      sort(joints);
    };

    const toggleRow = (detail) => {
      if (expandedRows.value.includes(detail)) {
        const index = expandedRows.value.findIndex((d) => d.uniqueKey == detail.uniqueKey);
        expandedRows.value.splice(index, 1);
      } else {
        expandedRows.value.push(detail);
      }
    };

    const getActiveJointDetails = () => {
      expandedRows.value = [];
      joints.value = cloneCurrentInspectionJoints();
      let activeJoints = [];

      //set unique key for toggle expansion and check for original
      if (joints.value) {
        for (const element of joints.value ?? []) {
          if (element?.jointId && element?.recordKey) {
            element.uniqueKey = element.jointId + "-" + element.recordKey;
            element.readOnly = true;
            if (!isNotCurrent(element)) {
              activeJoints.push(element);
            }
          }
        }
      }
      joints.value = activeJoints;
      sort(joints);
    };

    const hasReplacedJointDetails = () => {
      const clonedJoints = cloneCurrentInspectionJoints();

      if (clonedJoints) {
        const hasReplacedJointDetailsValue = clonedJoints.some(
          (element) => element.jointId && element.recordKey && isNotCurrent(element)
        );
        if (!hasReplacedJointDetailsValue) {
          showAllJoints.value = false;
        }
        return hasReplacedJointDetailsValue;
      }
      showAllJoints.value = false;
      return false;
    };

    const addNewJoint = () => {
      inspectionStore.setAddingOrEditing(true);
      snackBarId.value = snackbarStore.showMessage({
        displayText: INSPECTION_MESSAGES.JOINTS_DATATABLE,
      });
      isEditing.value = true;
      let newJoint = getNewJoint();
      newJoint.brkey = jointsValue.value?.brkey;
      newJoint.inspectionId = jointsValue.value?.inspectionId;
      newJoint.jointId = getNextjointId() + 1;
      newJoint.recordKey = "1";
      newJoint.isAdd = true;
      newJoint.uniqueKey = newJoint.jointId + "-" + newJoint.recordKey;
      newJoint.readOnly = false;
      joints.value.unshift(newJoint);
      previousSort.value = sortBy.value.map((x) => x);
      sortBy.value = [];
      expandedRows.value = [newJoint];
      scrollToDataTable("joints-data-table");
      jointsFooter.value?.setPageValue(1);

      nextTick(() => {
        detailForm.value.validate();
      });
    };

    const closeDetail = () => {
      if (isEditable.value && changesDetected.value) {
        //show confirmation dialog on closing
        closeConfirm.value
          .open(INSPECTION_MESSAGES.DETECT_DATATABLE_CHANGE_CONFIRMATION_MESSAGE, BUTTONS.YES)
          .then((response) => {
            if (!response) {
              getJointDetails();
              expandedRows.value = [];
              inspectionStore.setAddingOrEditing(false);
              snackbarStore.removeMessage(snackBarId.value);
              sortBy.value = previousSort.value?.map((x) => x);
              isEditing.value = false;
            }
          });
      } else {
        getJointDetails();
        expandedRows.value = [];
        inspectionStore.setAddingOrEditing(false);
        snackbarStore.removeMessage(snackBarId.value);
        sortBy.value = previousSort.value?.map((x) => x);
        isEditing.value = false;
      }
    };

    const saveDetail = (detail) => {
      detailForm.value
        .validate()
        .then(() => {
          if (validDetails.value) {
            validationSnackbar.value = false;
            changesDetected.value = false;
            if (detail.isAdd) {
              delete detail.isAdd;
              jointsValue.value.structureJoints.push(detail);
            } else {
              const updateIndex = jointsValue.value.structureJoints.findIndex(
                (a) => a.jointId === detail.jointId && a.recordKey === detail.recordKey
              );
              jointsValue.value.structureJoints.splice(updateIndex, 1, detail);
            }

            //if all required fields entered - close the expanded row
            expandedRows.value = [];
            isEditing.value = false;
            inspectionStore.setAddingOrEditing(false);
            snackbarStore.removeMessage(snackBarId.value);
            getJointDetails();
            sortBy.value = previousSort.value?.map((x) => x);
          } else {
            validationSnackbar.value = true;
          }
        })
        .catch((err) => {
          LOGGER.logException(err);
          changesDetected.value = false;
        });
    };

    const editDetail = (detail) => {
      previousSort.value = sortBy.value.map((x) => x);
      inspectionStore.setAddingOrEditing(true);
      snackBarId.value = snackbarStore.showMessage({
        displayText: INSPECTION_MESSAGES.JOINTS_DATATABLE,
      });
      isEditing.value = true;
      detail.readOnly = false;
      //if all required fields entered - close the expanded row
      expandedRows.value = [detail];
      nextTick(() => {
        detailForm.value?.validate();
      });
      sortBy.value = [];
      moveEditableElementToTop(detail);
      jointsFooter.value?.setPageValue(1);
    };

    const moveEditableElementToTop = (detail) => {
      const index = joints.value.findIndex((a) => a.jointId === detail.jointId && a.recordKey === detail.recordKey);
      let editableElement = joints.value[index];
      //then remove that element and use unshift
      joints.value.splice(index, 1);
      joints.value.unshift(editableElement);
    };

    const replaceDetail = (detail) => {
      previousSort.value = sortBy.value.map((x) => x);
      inspectionStore.setAddingOrEditing(true);
      snackBarId.value = snackbarStore.showMessage({
        displayText: INSPECTION_MESSAGES.JOINTS_DATATABLE,
      });
      isEditing.value = true;
      let newJoint = getNewJoint();
      newJoint.isAdd = true;
      newJoint.isReplacing = true;
      newJoint.brkey = detail.brkey;
      newJoint.inspectionId = detail.inspectionId;
      newJoint.jointId = detail.jointId;
      newJoint.recordKey = (getNextRecordKey(detail.jointId) + 1).toString();
      newJoint.uniqueKey = newJoint.jointId + "-" + newJoint.recordKey;
      newJoint.jointLocation = detail.jointLocation;
      newJoint.readOnly = false;
      joints.value.push(newJoint);
      expandedRows.value = [newJoint];
      sort(joints);
      nextTick(() => {
        detailForm.value.validate();
      });
    };

    const deleteDetail = (detail) => {
      const deleteIndex = jointsValue.value.structureJoints?.findIndex(
        (a) => a.jointId === detail.jointId && a.recordKey === detail.recordKey
      );
      if (deleteIndex > -1) {
        jointsValue.value.structureJoints.splice(deleteIndex, 1);
        expandedRows.value = [];
        getJointDetails();
      }
    };

    let isEditing = computed({
      get() {
        return inspectionLinksStore.isAddingOrEditingJoints;
      },
      set(value) {
        inspectionLinksStore.isAddingOrEditingJoints = value;
      },
    });
    const getNextjointId = () => {
      if (joints.value) {
        return Math.max(...joints.value.map((jointDetail) => convertToInt(jointDetail?.jointId ?? 0)), 0);
      } else {
        return 0;
      }
    };
    const getNextRecordKey = (jointId) => {
      let jointDetails = joints.value.filter((jointDetail) => jointDetail.jointId == jointId);
      return Math.max(...jointDetails.map((jointDetail) => convertToInt(jointDetail.recordKey || 0)));
    };

    const isExpanded = (details) => {
      return (
        expandedRows.value?.findIndex((a) => a.jointId == details.jointId && a.recordKey === details.recordKey) >= 0
      );
    };

    const rowClass = (data) => {
      return isNotCurrent(data) && !data.isAdd ? "rowReplaced" : "rowCurrent";
    };

    const anyJointDetailEditable = computed(() => {
      return expandedRows.value?.some((element) => !element.readOnly);
    });

    const getJointDetails = () => {
      if (showAllJoints.value) {
        getAllJointDetails();
      } else {
        getActiveJointDetails();
      }
    };
    const toggleAllPanels = (expandAll) => {
      if (expandAll) {
        panel.value = allPanels;
      } else {
        panel.value = [];
      }
    };

    const printD491 = () => {
      return { reportPage: "D491-Insp-Joints-Screen", marker: SNACKBAR_MARKER.JOINTS };
    };

    watch(
      () => [jointsValue],
      () => {
        // This watch will NOT be triggered during initial onMounted()
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          // for management module
          if (resetDirty.value) {
            inspectionLinksStore.isDirty = false;
            resetDirty.value = false;
          } else {
            inspectionLinksStore.isDirty = true;
          }
        } else if (props.moduleType === MODULE_TYPES.INSPECTION) {
          // for inspection  module
          if (resetDirty.value) {
            inspectionStore.setDirtyFlag(false);
            resetDirty.value = false;
          } else {
            inspectionStore.setDirtyFlag(true);
          }
        }
      },
      { deep: true }
    );
    // watch those objects used in component from structure to warn about unsaved changes
    watch(
      () => [props],
      () => {
        readData();
      },
      { deep: true }
    );

    return {
      isReplacement,
      panel,
      REFERENCE_TABLE,
      isEditable,
      structure,
      INSPECTION_COMMENTS,
      joints,
      onRowExpand,
      expandedRows,
      addNewJoint,
      validDetails,
      isExpanded,
      configStore,
      closeDetail,
      saveDetail,
      editDetail,
      replaceDetail,
      deleteDetail,
      isNotCurrent,
      jointCommentMaxLength,
      inspCommentMaxLength,
      rowClass,
      anyJointDetailEditable,
      detailForm,
      changesDetected,
      showAllJoints,
      getAllJointDetails,
      getActiveJointDetails,
      closeConfirm,
      BUTTONS,
      inspectionStore,
      INSPECTION_MESSAGES,
      getJointDetails,
      toggleRow,
      hasReplacedJointDetails,
      valueInRangeRule,
      pageInspection,
      jointsFooter,
      sortBy,
      jointConditions,
      valid,
      validate,
      toggleAllPanels,
      readData,
      saveChanges,
      cancelChanges,
      jointsValue,
      MODULE_TYPES,
      setUpdatedComment,
      printD491,
    };
  },
  components: {
    DataTableHeader,
    LabelSelect,
    CommentTypeTextField,
    LabelText,
    LabelCheckbox,
    IconTooltip,
    ConfirmDialog,
    DataTableFooter,
    ExpandCollapseToggle,
    ManagementCommentTypeTextField,
    ValidationCountsHeader,
  },
};
</script>
<style scoped lang="scss">
@use "@/styles/colors" as c;
.compactTable :deep(.v-input__control) {
  display: block !important;
}
.alignCenter {
  text-align: center;
}
.v-data-table :deep(button:hover) {
  background-color: inherit !important;
  color: inherit !important;
}

.v-data-table :deep(button:focus) {
  outline: none !important;
}
.tableHeader {
  text-align: center !important;
  background: c.$p-pa-light-blue !important;
}
</style>
